import {
  Button,
  Col,
  Container,
  Form,
  FormControl,
  InputGroup,
  Row,
} from 'react-bootstrap'
import React, { FC, useContext, useEffect, useState } from 'react'
import { FieldValue, FieldValues, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import {
  getJobErrors,
  getJobLoading,
} from '@editor/store/selectors/job.selector'
import {
  fetchAllVoices,
  selectAllVoices,
  VoiceModel,
} from '@editor/store/slices/voice.slice'
import { AppDispatch } from '@store/store'
import { actualizeJob, createFromUrl } from '@editor/store/thunks/job.thunk'
import { unwrapResult } from '@reduxjs/toolkit'
import { useNavigate, useLocation } from 'react-router-dom'
import { JobModel } from '@editor/store/slices/job.slice'
import {
  fetchAllTemplates,
  selectAllTemplates,
  TemplateModel,
} from '@editor/store/slices/template.slice'
import { toast } from 'react-toastify'
import {
  EditorContext,
  EditorContextType,
} from '@editor/components/editorContext'
import { fetchSsmlById } from '@editor/store/slices/ssml.slice'
import { fetchArticleById } from '@editor/store/slices/article.slice'
import { Tooltip } from 'react-tooltip'
import { getVoicesLoading } from '@editor/store/selectors/voice.selectior'
import { getTemplatesLoading } from '@editor/store/selectors/template.selector'

const HeaderBar: FC = () => {
  const {
    register,
    setError,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors, dirtyFields },
  } = useForm()
  const { job, article, ssml } = useContext(EditorContext) as EditorContextType
  const dispatch: AppDispatch = useDispatch()
  const jobError = useSelector(getJobErrors)
  const jobLoading = useSelector(getJobLoading)
  const voices = useSelector(selectAllVoices)
  const voicesLoading = useSelector(getVoicesLoading)
  const templates = useSelector(selectAllTemplates)
  const templatesLoading = useSelector(getTemplatesLoading)
  const navigate = useNavigate()
  const location = useLocation()
  const [loadingFromQuery, setLoadingFromQuery] = useState<boolean>(false)

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)
    const articleUrl = queryParams.get('articleUrl')

    if (articleUrl) {
      // TODO This should ensures the toast is fired just once, but it is fired twice
      if (!loadingFromQuery) {
        toast.info('Vyberte template a hlas pro načtení článku')
      }
      setLoadingFromQuery(true)
      setValue('urlInput', articleUrl)
    }

    if (voices.length == 0 && !voicesLoading) {
      dispatch(fetchAllVoices())
    }
    if (templates.length == 0 && !templatesLoading) {
      dispatch(fetchAllTemplates())
    }
  }, [dispatch, location.search, setValue])

  useEffect(() => {
    if (job) {
      setValue('templateSelect', job.usedTemplateName)
      setValue('voiceSelect', job.usedVoiceName)
      setValue('secondaryVoiceSelect', job.usedSecondaryVoiceName)
    }
  }, [job])

  const submitUrl = (data: FieldValue<FieldValues>) => {
    const { urlInput, voiceSelect, secondaryVoiceSelect, templateSelect } = data
    dispatch(
      createFromUrl({
        articleUrl: urlInput,
        voice: voiceSelect,
        template: templateSelect,
        secondaryVoice: secondaryVoiceSelect,
      })
    )
      .then(unwrapResult)
      .then(onSubmitUrlSuccess)
      .catch(onSubmitUrlError)
  }

  const onSubmitUrlSuccess = (job: JobModel) => {
    navigate(`/editor/${job.id}`)
    reset()
    toast.success(`Článek ${job.id} načten.`)
  }

  const onSubmitUrlError = (error: any) => {
    if (typeof error === 'string')
      setError('urlInput', {
        type: 'custom',
        message: error,
      })
    toast.error(error)
    console.error(`Článek se nepodařilo načíst. ${JSON.stringify(error)}`)
  }

  const onActualizeClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    const res = window.confirm(
      'Aktualizace článku stáhne aktuální verzi článku a přepíše obsah editoru i SSML. Přejete si pokračovat?'
    )
    if (!res) return
    if (job) {
      const { templateSelect, voiceSelect, secondaryVoiceSelect } = getValues()
      dispatch(
        actualizeJob({
          jobId: job.id,
          voice: voiceSelect,
          template: templateSelect,
          secondaryVoice: secondaryVoiceSelect || null,
        })
      )
        .then(unwrapResult)
        .then(onActualizeSuccess)
        .catch(onActualizeError)
    }
  }

  const onActualizeSuccess = () => {
    toast.success('Článek aktualizován')
    if (ssml?.id) {
      dispatch(fetchSsmlById(ssml?.id))
    }
    if (job?.article) {
      dispatch(fetchArticleById(job.article))
    }
  }

  const onActualizeError = (error: any) => {
    toast.error('Článek se nepodařilo aktualizovat:', error)
  }

  const handleNewJobClick = () => {
    document.location.href = '/editor/'
  }

  return (
    <div className="bg-light-subtle text-bg-dark">
      <Form>
        <Container className="app-max-width py-3">
          <Row>
            <Col className="d-flex flex-column">
              <InputGroup>
                <FormControl
                  {...register('urlInput', { required: true })}
                  placeholder="Vložte url"
                  className={errors.urlInput ? 'is-invalid' : ''}
                  defaultValue={article?.url}
                  disabled={!!job && !!article?.url}
                ></FormControl>
              </InputGroup>
              <Form.Control.Feedback type="invalid" className="ps-2">
                {errors.urlInput ? errors.urlInput.message?.toString() : ''}
              </Form.Control.Feedback>
            </Col>
          </Row>
          <Row
            style={{
              width: '95%',
              margin: '3px auto',
            }}
          >
            <Col>
              <InputGroup>
                <InputGroup.Text>Template</InputGroup.Text>
                <Form.Control
                  {...register('templateSelect', { required: true })}
                  as="select"
                  disabled={false}
                  defaultValue={job ? job.usedTemplateName : ''}
                >
                  <option value="" disabled hidden>
                    Vyberte template
                  </option>
                  {templates.length > 0 &&
                    templates.map((template: TemplateModel) => (
                      <option
                        key={'t' + template.id}
                        value={template.templateName}
                      >
                        {template.templateName}
                      </option>
                    ))}
                </Form.Control>
                <InputGroup.Text>Hlas</InputGroup.Text>
                <Form.Control
                  as="select"
                  {...register('voiceSelect', { required: true })}
                  disabled={false}
                  defaultValue={job ? job.usedVoiceName : ''}
                >
                  <option value="" disabled hidden>
                    Vyberte hlas
                  </option>
                  {voices.map((voice: VoiceModel) => (
                    <option key={'v' + voice.id} value={voice.value}>
                      {voice.name}
                    </option>
                  ))}
                </Form.Control>
                <InputGroup.Text>Citace</InputGroup.Text>
                <Form.Control
                  as="select"
                  {...register('secondaryVoiceSelect', { required: false })}
                  disabled={false}
                  defaultValue={job ? job.usedSecondaryVoiceName : ''}
                  placeholder="Výchozí hlas citací"
                >
                  <option value="" disabled hidden>
                    Výchozí
                  </option>
                  {voices.map((voice: VoiceModel) => (
                    <option key={'sv' + voice.id} value={voice.value}>
                      {voice.name}
                    </option>
                  ))}
                </Form.Control>
                {!job && (
                  <Button
                    onClick={handleSubmit(submitUrl)}
                    variant="primary"
                    type="submit"
                    disabled={jobLoading}
                  >
                    <strong>Načíst</strong>
                  </Button>
                )}
                {!!job && (
                  <>
                    <Button
                      onClick={onActualizeClick}
                      variant="outline-warning"
                      type="submit"
                      className="fw-bold"
                      data-tooltip-content="Stáhne nejnovější verzi článku a přepíše momentální stav"
                      data-tooltip-id="actualize-tooltip"
                      data-tooltip-place="top"
                      style={{ marginRight: '1px' }}
                    >
                      Aktualizovat
                    </Button>
                    <Tooltip id="actualize-tooltip" />
                    <Button
                      variant="success"
                      onClick={() => handleNewJobClick()}
                    >
                      <strong>Nový článek</strong>
                    </Button>
                  </>
                )}
              </InputGroup>
            </Col>
          </Row>
        </Container>
      </Form>
    </div>
  )
}

export default HeaderBar
